import authService from '@platform/core/src/services/authService';
import config from '@platform/core/src/config';

const API_BASE_URL = config.API_BASE_URL;
const TRACKABLES_API_URL = `${API_BASE_URL}/api/v1/trackables`;

/**
 * Fetches the definitions of all trackable items (system + user custom).
 * @returns {Promise<Array>} List of trackable definitions.
 */
export const getTrackableDefinitions = async () => {
  try {
    const response = await authService.authFetch(`${TRACKABLES_API_URL}/definitions`);
    if (!response.ok) {
      throw new Error(`Failed to fetch trackable definitions: ${response.status}`);
    }
    return await response.json();
  } catch (error) {
    console.error('[trackablesApi] Error fetching definitions:', error);
    // Fallback/Mock for development if API isn't ready yet
    return [];
  }
};

/**
 * Fetches a single trackable definition by ID.
 * @param {string} id - The definition ID
 * @returns {Promise<Object>} The trackable definition.
 */
export const getTrackableDefinitionById = async (id) => {
  try {
    const response = await authService.authFetch(`${TRACKABLES_API_URL}/definitions/${id}`);
    if (!response.ok) {
      throw new Error(`Failed to fetch trackable definition: ${response.status}`);
    }
    return await response.json();
  } catch (error) {
    console.error('[trackablesApi] Error fetching definition:', error);
    throw error;
  }
};

/**
 * Creates a new custom trackable definition.
 * @param {Object} definitionData - The definition data
 * @returns {Promise<Object>} The created definition.
 */
export const createTrackableDefinition = async (definitionData) => {
  try {
    const response = await authService.authFetch(`${TRACKABLES_API_URL}/definitions`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(definitionData),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `Failed to create trackable: ${response.status}`);
    }
    return await response.json();
  } catch (error) {
    console.error('[trackablesApi] Error creating definition:', error);
    throw error;
  }
};

/**
 * Updates an existing custom trackable definition.
 * @param {string} id - The definition ID
 * @param {Object} definitionData - The updated definition data
 * @returns {Promise<Object>} The updated definition.
 */
export const updateTrackableDefinition = async (id, definitionData) => {
  try {
    const response = await authService.authFetch(`${TRACKABLES_API_URL}/definitions/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(definitionData),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `Failed to update trackable: ${response.status}`);
    }
    return await response.json();
  } catch (error) {
    console.error('[trackablesApi] Error updating definition:', error);
    throw error;
  }
};

/**
 * Deletes a custom trackable definition.
 * @param {string} id - The definition ID
 * @returns {Promise<void>}
 */
export const deleteTrackableDefinition = async (id) => {
  try {
    const response = await authService.authFetch(`${TRACKABLES_API_URL}/definitions/${id}`, {
      method: 'DELETE',
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `Failed to delete trackable: ${response.status}`);
    }
    // Some DELETE endpoints return 204 No Content
    if (response.status === 204) {
      return;
    }
    return await response.json();
  } catch (error) {
    console.error('[trackablesApi] Error deleting definition:', error);
    throw error;
  }
};